// Pure form logic for the propose / add vendor drawer: starting values, the
// changed-fields diff, and inline validation. Kept out of the component so the
// rules are testable and the component stays presentational.

export const NOTE_MAX = 500;

// Field groups (spec 4.2). `kind` drives the control; `iso` fields are
// read-only when Odoo has no ISO fields yet.
export const FORM_GROUPS = [
  {
    title: 'Identity',
    fields: [
      { key: 'name', label: 'Name', kind: 'text', max: 200 },
      { key: 'is_company', label: 'Is a company', kind: 'bool' },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { key: 'email', label: 'Email', kind: 'email', max: 200 },
      { key: 'phone', label: 'Phone', kind: 'text', max: 50 },
      { key: 'website', label: 'Website', kind: 'url', max: 200 },
      { key: 'street', label: 'Street', kind: 'text', max: 200 },
      { key: 'street2', label: 'Street 2', kind: 'text', max: 200 },
      { key: 'city', label: 'City', kind: 'text', max: 100 },
      { key: 'zip', label: 'ZIP', kind: 'text', max: 20 },
      { key: 'country_id', label: 'Country', kind: 'country' },
      { key: 'state_id', label: 'State', kind: 'state' },
    ],
  },
  {
    title: 'Tax & legal',
    fields: [
      { key: 'vat', label: 'Tax ID', kind: 'text', max: 50 },
      { key: 'company_registry', label: 'Company registry', kind: 'text', max: 50 },
    ],
  },
  {
    title: 'ISO compliance',
    iso: true,
    fields: [
      { key: 'risk_level', label: 'Risk level', kind: 'select', options: 'RISK' },
      { key: 'data_access_scope', label: 'Data access scope', kind: 'select', options: 'DATA_ACCESS' },
      { key: 'nda_status', label: 'NDA status', kind: 'select', options: 'NDA' },
      { key: 'nda_date', label: 'NDA date', kind: 'date' },
      { key: 'contract_status', label: 'Contract status', kind: 'select', options: 'CONTRACT' },
      { key: 'contract_end', label: 'Contract end', kind: 'date' },
    ],
  },
];

const ALL_FIELDS = FORM_GROUPS.flatMap((g) => g.fields.map((f) => ({ ...f, iso: !!g.iso })));

const isoDate = (v) => (v ? String(v).slice(0, 10) : '');

export function initialValues(vendor) {
  const out = {};
  ALL_FIELDS.forEach((f) => {
    if (f.kind === 'bool') out[f.key] = vendor ? vendor.is_company !== false : true;
    else if (f.kind === 'date') out[f.key] = isoDate(vendor?.[f.key]);
    // The mirror carries country/state names, not ids: a picker starts empty
    // and only sends a value when the person picks one.
    else if (f.kind === 'country' || f.kind === 'state') out[f.key] = '';
    else out[f.key] = vendor?.[f.key] ?? '';
  });
  return out;
}

// Which fields go in the proposal. Create: whatever was filled in. Edit: only
// what differs from the starting values. Selects, dates and ids can't be
// cleared through a proposal, so an empty one is left out rather than sent.
export function buildChanges(initial, values, { mode, isoKnown }) {
  const changes = {};
  ALL_FIELDS.forEach((f) => {
    if (f.iso && !isoKnown) return;
    const v = values[f.key];
    if (f.kind === 'bool') {
      if (mode === 'create' || v !== initial[f.key]) changes[f.key] = v;
      return;
    }
    const blank = v === '' || v === null || v === undefined;
    if (f.kind === 'text' || f.kind === 'email' || f.kind === 'url') {
      if (mode === 'create' ? !blank : v !== initial[f.key]) changes[f.key] = typeof v === 'string' ? v.trim() : v;
      return;
    }
    if (blank) return;
    if (mode === 'create' || v !== initial[f.key]) {
      changes[f.key] = f.kind === 'country' || f.kind === 'state' ? Number(v) : v;
    }
  });
  return changes;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validUrl(v) {
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    return u.hostname.includes('.');
  } catch {
    return false;
  }
}

function validDate(v) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

export function validate(values, { tagIds, isoKnown }) {
  const errors = {};
  if (!String(values.name || '').trim()) errors.name = 'Name is required.';
  if (!tagIds.length) errors.tags = 'Pick at least one speciality.';
  if (values.email && !EMAIL_RE.test(values.email.trim())) errors.email = 'Enter a valid email address.';
  if (values.website && !validUrl(values.website.trim())) errors.website = 'Enter a valid website address.';
  if (isoKnown) {
    if (values.nda_date && !validDate(values.nda_date)) errors.nda_date = 'Enter a valid date.';
    if (values.contract_end && !validDate(values.contract_end)) errors.contract_end = 'Enter a valid date.';
  }
  return errors;
}

// High/Critical with an NDA that isn't signed: a warning, never a block.
export const needsNdaWarning = (values) => (
  (values.risk_level === 'high' || values.risk_level === 'critical') && values.nda_status !== 'signed'
);

export const sameIds = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
